import { Component, inject, Input, OnInit } from '@angular/core';
import { WorkflowPageNavigationService } from '../../../../service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowWidgetEventServiceService } from '../../../../widget/workflow-widget-event.service';
import { FocusService, GoogleTagManagerService } from '@cudirect/shared-ui-library';
import { dependencies } from '../../../../class/workflow.dependency';
import { CorePageBaseComponent } from '../core-page-base.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';

@Component({
  selector: 'cu-dynamic-page-action-bar',
  templateUrl: './page-action-bar.component.html'
})
export class PageActionBarComponent extends CorePageBaseComponent implements OnInit {
  public statefulService = inject(StatefulService);
  public analyticsMetadataService = inject(AnalyticsMetadataService);

  constructor(
    public workflowPageNavigationService: WorkflowPageNavigationService,
    public readonly EventService: WorkflowWidgetEventServiceService,
    private readonly focusService: FocusService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly workflowLoader: WorkflowLoaderService,
    private readonly gtm: GoogleTagManagerService
  ) {
    super(workflowPageNavigationService, EventService);
  }

  /**
   * indicate if action bar should be visible
   */
  @Input() public showActionBar = true;

  private reviewLabel: string;
  /**
   * Loading state of the button
   */
  public isLoading = false;
  /**
   * Disabled state of the button
   */
  public isDisabled = false;

  public removeCoApplicantLabel: string;

  public ngOnInit(): void {
    const page = this.workflowPageNavigationService.getReviewPage();
    if (page) {
      this.reviewLabel = `Back to ${page.title}`;
    }
    // Adding Router events here so we can track when the page navigation ends
    // This will allow better control ocer when to re-enable the buttons
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.loadingNextButton(false);
      }
    });

    this.removeCoApplicantLabel = this.getRemoveCoAppTitle();
    this.handleStreamlined();
  }

  private get isReviewPage() {
    return this.page?.config?.customPageType === 'legacy-review';
  }
  /** continue button label  */
  public get continueLabel(): string {
    if (!this.page.config.next) {
      return 'Save and Continue';
    }

    /** application review page update button label */
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      if (!this.isReviewPage) {
        return 'Save Changes';
      }
    }

    if (typeof this.page.config.next === 'string') {
      return this.page.config.next;
    }

    return dependencies.logicService.evaluate(this.page.config.next, { page: this.page }, 'single', null, this.page.pageIterationId)[0];
  }

  /** back button label */
  public get backLabel(): string {
    /** application review page update button label */
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      if (!this.isReviewPage) {
        return this.reviewLabel || 'Back to Application Review';
      }
    }

    return this.page.config.previous;
  }

  /**
   * Set the loading and disabled state of the Next button
   * @param active boolean
   */
  private loadingNextButton(active: boolean): void {
    this.isDisabled = active;
    this.isLoading = active;
  }

  /** When Back is clicked */
  public onBackClicked(): void {
    this.widgetEventService.emitPreviousButtonClickedClicked({ flow: this.flow, page: this.page });
    if (this.page.config.pagePreviousClicked) {
      this.evaluateClickedFn(this.page.config.pagePreviousClicked, this.navigationPrevious);
      return;
    }

    this.navigationPrevious();
  }

  /**
   * Since "EA" button component disabledButton still triggers onClick
   * handler (as a bug), makes sure page is complete befor executing continue logic
   * @returns
   */
  public onContinueClicked(): void {
    // If PID Verifiction page, DO NOT disable the button on click
    if (this.page.nextPage?.path !== 'application-review') {
      this.loadingNextButton(true);
    }

    // Page submit will trigger page
    if (this.alwaysEnablePageNext) {
      this.workflowPageNavigationService.pageSubmitted = true;
    }

    // If the page is not complete, then set to disabled
    if (!this.page.helper.isPageComplete) {
      // If there are invalid fields, find the first and focus on click
      if (this.page.helper.invalidFields?.length > 0) {
        const focusId =
          this.page.helper.invalidFields[0]?.config?.type?.toLowerCase() === 'radio'
            ? this.page.helper.invalidFields[0].id + '-0'
            : this.page.helper.invalidFields[0].id;
        this.focusService.setFocus(focusId);
      }
      this.loadingNextButton(false);
      return;
    }

    const data = this.analyticsMetadataService.getMetadata(this.page);
    this.gtm.publishEvent(data[0], data[1]);

    if (this.page.helper.touchedFields.length === 0) {
      /// marking page touched when user clicks continue button
      this.page.dirty = true;
    }
    this.workflowPageNavigationService.pageSubmitted = true;
    this.widgetEventService.emitNextButtonClicked({ flow: this.flow, page: this.page });

    if (this.page.config.pageNextClicked) {
      this.evaluateClickedFn(this.page.config.pageNextClicked, this.navigationNext);
      return;
    }

    this.navigationNext();
  }

  private evaluateClickedFn(logicFn, defaultNavigationFn?: () => void) {
    // Timeout to allow events to complete and virtual keyboard to close on mobile
    // before landing on the previous page so that scroll position does not persist
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dependencies.logicService.evaluate<Promise<any>>(
        logicFn,
        {
          page: this.page,
          flow: this.flow,
          defaultNavigationFn,
          pageIteration: this.page.pageIterationId
        },
        'generic',
        null,
        this.page.pageIterationId
      );

      // Reset the button state if a modal is opened
      if (this.page?.modalVisible) {
        this.loadingNextButton(false);
      }
    }, 400);
  }

  private get alwaysEnablePageNext(): boolean {
    return this.flow.config.alwaysEnablePageNext || this.page.config.alwaysEnablePageNext;
  }

  private navigationNext = () => {
    this.workflowPageNavigationService.updateNavState(this.page, true);
    if (this.page.helper.isPageComplete && this.page.helper.hasNextPage) {
      this.workflowPageNavigationService.navigateNextPage(!this.isReviewPage && this.route.snapshot.fragment);
    }
  };

  private navigationPrevious = () => {
    if (this.page.helper.hasPreviousPage) {
      this.workflowPageNavigationService.navigatePreviousPage(!this.isReviewPage && this.route.snapshot.fragment);
    }
  };

  public get previousPageLabel(): string {
    return this.page.previousPage.title ? this.page.previousPage.customTitleOrDefault : 'previous page';
  }

  /**
   * Check Previous Page button visibility
   * @returns boolean
   */
  public checkPreviousPageButtonVisibility(): boolean {
    const isEditReview = this.statefulService.wrap('ss:edit-review-page').value;
    // Do not show back button if previous page is in stream skip logic.
    // Show Backbutton when user edit from review
    if (this.flow?.config?.streamlined && !isEditReview && !this.route.snapshot.fragment) {
      if (
        (!!this.page.previousPage && this.flow.config.streamlinedSkipPages?.includes(this.page.previousPage.config.id)) ||
        this.flow.config.streamlinedSkipPages?.includes(this.page.config.id)
      ) {
        return false;
      }
    }

    if (isEditReview) {
      const firstInCompletePage = this.workflowPageNavigationService.getFirstInCompletePage();
      if (firstInCompletePage) {
        if (!firstInCompletePage.id.includes('review')) {
          return false;
        }
      }
    }
    return this.page.helper.isPreviousButtonVisible;
  }

  /**
   * Retrieves the title for the action to remove a co-applicant.
   * If there is an additional co-applicant, the title will indicate the removal of the second co-applicant.
   * Otherwise, it will indicate the removal of a single co-applicant.
   * @returns The title for the remove co-applicant action.
   * */
  public getRemoveCoAppTitle(): string {
    const hasAdditionalCoApplicant = this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant').value as boolean;
    const isAdditionalCoApplicantPage = this.page.id.includes('oao-application-fields-additional-co-app');
    if (hasAdditionalCoApplicant && isAdditionalCoApplicantPage) {
      return 'Remove second co-applicant and continue';
    }
    return 'Remove co-applicant and continue';
  }

  /**
   * This will handle some of the logic if we are in a streamlined template
   * First - check if streamlined, then show the loader, verify if we should be showing the loader
   * we use an interval specifically for the initial template loading as we can accidentally get stuck
   * if the data loads before the workflow - technically could be done without the interval in later pages.-
   * Once we know the page is loaded, we check if we can skip - behind the loader.
   */
  private handleStreamlined(): void {
    this.flow?.closeLoader();
    // here we call continue
    if (this.flow?.config?.streamlined && (!this.route.snapshot.fragment || this.page.config.lockOnPrefill)) {
      const streamlineSkip = this.statefulService.wrap(this.flow.config.streamlinedPrefillPath, null, this.page.pageIterationId);
      if (this.flow.config.streamlinedSkipPages.includes(this.page.id)) {
        // we can't show this on every page - so ensure we want to trigger by supported skip page
        this.handleLoader(true, streamlineSkip.value === 'Prefill');
      }
      const pollHandle = setInterval(() => {
        const skipPageSupported = streamlineSkip.value;
        if (skipPageSupported === 'Prefill' && this.flow.config.streamlinedSkipPages.includes(this.page.id)) {
          this.handleLoader(true, skipPageSupported === 'Prefill');
          if (!this.page.helper.pageVisibleFields || this.page.helper.pageVisibleFields.length === 0) {
            return;
          }
          clearInterval(pollHandle);
          // check if the page is valid, if not, they should add missing data
          if (this.page.helper.isPageComplete) {
            // we are good to skip page
            this.onContinueClicked();
            return;
          }
        }
        clearInterval(pollHandle);
        this.handleLoader(false);
      }, 20);
      return;
    }
    if (this.page.id === 'oao-membership' && this.flow.id === 'oao' && !!sessionStorage.getItem('MembershipType')) {
      this.handleLoader(false);
    }
  }

  /** only call this if in streamlined, handles showing or hiding the loader */
  private handleLoader(show: boolean = false, prefill: boolean = false): void {
    const messages = [
      'Connecting to your profile...',
      'Retrieving your information...',
      'Importing to your application...',
      'Importing to your application...'
    ];
    let page = this.flow?.config?.streamlinedSkipPages?.indexOf(this.page.id);
    if (typeof page === 'undefined') page = -1;
    let message = page > -1 ? messages[page] : '';
    if (!prefill) {
      message = 'Loading please wait...';
    }
    if (show && page === 0) {
      // first page, start loading animation
      this.setLoaderforFirstPageWhenShow(message, prefill ? 'documentscan' : 'padded');
    } else if (show && page === 1) {
      this.setLoaderforSecondPageWhenShow(message, prefill ? 'documentscan' : 'default');
    } else if (show) {
      this.setLoaderDefaultWhenShow(page, message, prefill ? 'documentscan' : 'default');
    } else if (this.page.id !== 'oao-additional-accounts') {
      this.workflowLoader.end(message);
    }
  }

  /** Set workflowloader and message when page is 0 position of skip pages*/
  private setLoaderforFirstPageWhenShow(message: string, animationId: string) {
    this.workflowLoader.start(message, {
      animationId,
      overlayMode: 'card',
      segments: this.flow.config.streamlinedSkipPages.length - 1
    });
  }

  /** Set workflowloader and message when page is 1 position of skip pages*/
  private setLoaderforSecondPageWhenShow(message: string, animationId: string) {
    if (['hide', 'end', 'clear', 'init'].includes(this.workflowLoader.state.type)) {
      this.workflowLoader.start(message, {
        animationId,
        overlayMode: 'card',
        segments: this.flow.config.streamlinedSkipPages.length - 1
      });
    } else if (this.workflowLoader.segmentInfo.activeSegment === 0) {
      this.workflowLoader.next(message);
    }
  }

  /** Set workflowloader and message when show is true */
  private setLoaderDefaultWhenShow(page, message: string, animationId: string) {
    if (['init', 'start', 'next', 'show'].includes(this.workflowLoader.state?.type)) {
      if (this.workflowLoader.state.options?.segments !== page) {
        // advance because we are on another page
        this.workflowLoader.next(message);
      }
    } else {
      this.workflowLoader.start('Loading please wait...', {
        animationId,
        overlayMode: 'card',
        segments: this.flow.config.streamlinedSkipPages.length - 1
      });
    }
  }
}
